import { useMemo } from "react";
import { useSelector } from "react-redux";
import { RootState } from "@/store/store";
import { RoleHasPermission } from "@/types/auth.types";
import { isPlatformAdminUser } from "@/utils/userRole";

export const usePermissions = () => {
  const userPermissions = useSelector(
    (state: RootState) => state.auth.userPermissions,
  );
  const roles = useSelector((state: RootState) => state.auth.userData?.roles);
  const isPlatformAdmin = isPlatformAdminUser(roles);

  const hasPermission = useMemo(
    () =>
      (uniqueKey: string): boolean => {
        if (isPlatformAdmin) return true;
        if (!userPermissions || userPermissions.length === 0) return false;

        return userPermissions.some(
          (permission: RoleHasPermission) =>
            permission.permissions?.unique_key === uniqueKey,
        );
      },
    [userPermissions, isPlatformAdmin],
  );

  const hasAnyPermission = useMemo(
    () =>
      (uniqueKeys: string[]): boolean => {
        if (isPlatformAdmin) return true;
        if (!userPermissions || userPermissions.length === 0) return false;

        return uniqueKeys.some((uniqueKey) =>
          userPermissions.some(
            (permission: RoleHasPermission) =>
              permission.permissions?.unique_key === uniqueKey,
          ),
        );
      },
    [userPermissions, isPlatformAdmin],
  );

  const hasAllPermissions = useMemo(
    () =>
      (uniqueKeys: string[]): boolean => {
        if (isPlatformAdmin) return true;
        if (!userPermissions || userPermissions.length === 0) return false;

        return uniqueKeys.every((uniqueKey) =>
          userPermissions.some(
            (permission: RoleHasPermission) =>
              permission.permissions?.unique_key === uniqueKey,
          ),
        );
      },
    [userPermissions, isPlatformAdmin],
  );

  return {
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
  };
};
